import React from 'react';
// eslint-disable-next-line no-restricted-imports
import dayjs, { ManipulateType } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import minMax from 'dayjs/plugin/minMax';
import toObject from 'dayjs/plugin/toObject';
import arraySupport from 'dayjs/plugin/arraySupport';
import toArray from 'dayjs/plugin/toArray';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';
import { swapKeysAndValues } from './swap-keys-and-values';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimeRange } from '@uplift/types';

export const DateAdapter = AdapterDayjs;
export const dateAdapter = new DateAdapter();

export const getWeekdays = function () {
  const start = dateAdapter.dayjs().startOf('week');

  return [ 0, 1, 2, 3, 4, 5, 6 ].map((diff) =>
    dateAdapter.formatByString(dateAdapter.addDays(start, diff), 'dd'),
  );
};


dayjs.extend(arraySupport);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(minMax);
dayjs.extend(toObject);
dayjs.extend(toArray);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);

const tzAbbreviations = {
  'America/Anchorage': 'AK',
  'America/Chicago': 'CT',
  'America/New_York': 'ET',
  'Pacific/Honolulu': 'HAT',
  'America/Denver': 'MT',
  'America/Phoenix': 'MST',
  'America/Los_Angeles': 'PT',
} as const;

const abbreviationsToTz = swapKeysAndValues(tzAbbreviations);

// We can extend dayjs and add ability to render ET instead of EST/EDT when use `z` format
dayjs.extend((_options, dayjsClass, _dayjsFactory) => {
  const oldOffsetName = dayjsClass.prototype.offsetName;

  dayjsClass.prototype.offsetName = function (...args) {
    const abbr = tzAbbreviations?.[this?.$x?.$timezone];

    if (abbr) {
      return abbr;
    }

    return oldOffsetName.call(this, ...args);
  };
});

export type DateObjectDiffUnit = dayjs.QUnitType | dayjs.OpUnitType;
export type DateObjectInput = dayjs.ConfigType;
export type DateObject = dayjs.Dayjs;

export type TimeWindow = {
  start: DateObject;
  end: DateObject;
};

export enum DateFormats {
  month = 'YYYY-MM',
  date = 'YYYY-MM-DD',
  time = 'hh:mm A',
  dateTime = 'M/DD/YY hh:mm A',
  day = 'day',
}

const getMonthDatesRange = (
  format = DateFormats.date
): TimeRange => {
  return {
    // FIXME: revert when API will handle request for month
    start: dayjs().utc().startOf('week').format(format),
    end: dayjs().utc().endOf('week').format(format),
    // end: dayjs().endOf('month').format(format),
    // start: dayjs().startOf('month').format(format),
  };
};

const isTodayOrFutureDay = (date: string | Date): boolean => {
  const strDate = dayjs.utc(date).toString();

  return dayjs.utc(strDate).isSameOrAfter(dayjs().utc(), DateFormats.day);
};

const setHour = (forDate: string, forHour: string): string => {
  return dayjs(forDate).hour(dayjs(forHour).hour()).format();
};

const addHour = (date: string, hour = 1): DateObject => {
  return dayjs.utc(date).add(hour, 'hour');
};

const setMinutes = (
  forDate: string,
  minutes: number,
  isLocalTimeInUTC = false
): string => {
  return dayjs(forDate).utc(isLocalTimeInUTC).minute(minutes).format();
};

const applyTimezone = (date: DateObject, timezone?: string) => {
  return timezone ? date.tz(timezone) : date;
};

const format = (
  _date: DateObject | string,
  formatCurrentYear: string,
  formatDifferentYear?: string,
  opts?: {
    now?: DateObject;
    timezone?: string;
  },
) => {
  const now = applyTimezone(opts?.now ?? dayjs(), opts?.timezone);
  const date = applyTimezone(dayjs(_date), opts?.timezone);

  if (now.isSame(date, 'year')) {
    return date.format(formatCurrentYear);
  }

  return date.format(formatDifferentYear || formatCurrentYear);
};

const setTimeZone = (date: string, timezone = 'America/Denver') => {
  return dayjs.utc(date).tz(timezone);
};

const timezoneAbbreviation = (date: DateObject | Date | string, timezone: string) => {
  return dayjs(date).tz(timezone).format('z');
};

const getReschedulingTime = (
  date: string,
  time: string
): TimeRange => {
  const startHour = dayjs.utc(time).hour();
  const selectedDateTime = dayjs.utc(date).hour(startHour).format();

  return {
    start: setTimeZone(selectedDateTime).format(),
    end: setTimeZone(selectedDateTime).add(1, 'hour').format(),
  };
};

const now = (timezone: string) => dayjs().tz(timezone);
const getSecondsToNextMinute = () => 60 - new Date().getSeconds();

export type TimeRangeGenerator = (minutesStep?: number, inclusive?: boolean, dateStart?: DateObject) => dayjs.Dayjs[];

export const generate24HoursDayTimeRange: TimeRangeGenerator = (minutesStep = 15, inclusive = false, dateStart: DateObject = dayjs(0).startOf('day')) => {
  const items: DateObject[] = [];
  const minutesLeftInDay = 1440; // 24 hours * 60 minutes
  const iterationsCountShift = inclusive ? 0 : -1;
  const iterationsCount = minutesLeftInDay / minutesStep + iterationsCountShift;

  const minutesStepDifference = minutesLeftInDay % minutesStep;
  const start = dateStart.add(minutesStepDifference, 'minutes');

  for (let i = 0; i <= iterationsCount; i++){
    items.push(start.add(i * minutesStep, 'minutes'));
  }

  return items;
};

export const generateDayTimeRange: TimeRangeGenerator = (minutesStep = 15, inclusive = false, dateStart: DateObject = dayjs(0).startOf('day')) => {
  const items: DateObject[] = [];
  const minutesLeftInDay = dateStart.add(1, 'day').startOf('D').diff(dateStart, 'minutes');
  const iterationsCountShift = inclusive ? 0 : -1;
  const iterationsCount = minutesLeftInDay / minutesStep + iterationsCountShift;

  const minutesStepDifference = minutesLeftInDay % minutesStep;
  const start = dateStart.add(minutesStepDifference, 'minutes');

  for (let i = 0; i <= iterationsCount; i++){
    items.push(start.add(i * minutesStep, 'minutes'));
  }

  return items;
};

export type FormatTimeOpts = {
  forceMinutes?: boolean;
  showAmPm?: boolean;
};
export const formatTime = (
  time: DateObject,
  {
    forceMinutes = false,
    showAmPm = true
  }: FormatTimeOpts = {}
) => {
  const showMinutes = time.minute() > 0 || forceMinutes;

  if (showMinutes && showAmPm) {
    return time.format('h:mma');
  }
  if (!showMinutes && showAmPm) {
    return time.format('ha');
  }
  if (showMinutes && !showAmPm) {
    return time.format('h:mm');
  }

  return time.format('h');
};


export type FormatTimeRangeWithMeridiemOpts = {
  forceMinutes?: boolean;
  separator?: string;
};
export const formatTimeRangeWithMeridiem = (
  start: DateObject,
  end: DateObject,
  {
    forceMinutes = true,
    separator = ' – ',
  }: FormatTimeRangeWithMeridiemOpts = {}
) => {
  const startTime = formatTime(start, {
    forceMinutes: forceMinutes,
    showAmPm: start.format('a') !== end.format('a'),
  });
  const endTime = formatTime(end, { forceMinutes });

  return `${startTime}${separator}${endTime}`;
};


export const splitDateTime = (from: DateObject, to: DateObject, duration = 15) => {
  const result: DateObject[] = [];
  let curr = from;

  while (!curr.isSameOrAfter(to)) {
    result.push(curr);
    curr = curr.add(duration, 'minutes');
  }

  return result;
};

export const timeRangeToTimeWindow = (range: TimeRange): TimeWindow => {
  const start = dayjs(range.start, 'HH:mm:ss');
  const end = dayjs(range.end, 'HH:mm:ss').add(range.end === '00:00:00' ? 1 : 0, 'day');

  return { start, end };
};

export const timeWindowToTimeRange = (range: TimeWindow): TimeRange => {
  return {
    start: range.start.format('HH:mm:ss'),
    end: range.end.format('HH:mm:ss'),
  };
};

export const formatDateTimeRange = (dateTimeFrom: string, dateTimeTo: string, timezone: string) => {
  const fromDt = CalendarDateService.date(dateTimeFrom).tz(timezone);
  const toDt = CalendarDateService.date(dateTimeTo).tz(timezone);

  const fromDateString = fromDt.format('ddd, MMM D');
  const timeRange = formatTimeRangeWithMeridiem(fromDt, toDt, { forceMinutes: false, separator: '–' });

  return [ fromDateString, 'at', timeRange ].join(' ');
};

export const squashTimeRanges = (ranges: TimeRange[]) => {
  const timeRanges = ranges
    .map(CalendarDateService.timeRangeToTimeWindow);

  timeRanges.sort((a, b) => a.start.isSameOrAfter(b.start) ? 1 : -1);

  const result: TimeWindow[] = [];

  for (const item of timeRanges) {
    const last = result.pop();

    if (!last) {
      result.push(item);
      continue;
    }

    if (item.start.isSameOrAfter(last.start) && (item.start.isBefore(last.end) || item.start.isSame(last.end))) {
      result.push({
        ...last,
        end: CalendarDateService.date.max(last.end, item.end),
      });
      continue;
    }

    result.push(last);
    result.push(item);
  }

  return result.map(CalendarDateService.timeWindowToTimeRange);
};

export const range = (start: DateObject, end: DateObject, deltaValue: number, deltaType: ManipulateType = 'day') => {
  const result: DateObject[] = [];

  for (let i = start; i.isBefore(end) || i.isSame(end); i = i.add(deltaValue, deltaType)) {
    result.push(i);
  }

  return result;
};

export const CalendarDateService = {
  date: dayjs,
  time: (time: string, opts?: { base?: DateObject; nextDayIfMidnight?: boolean }) => {
    let result = dayjs(time, 'HH:mm:ss');

    if (opts?.base) {
      result = result
        .set('year', opts.base.year())
        .set('month', opts.base.month())
        .set('date', opts.base.date());
    }

    if (opts?.nextDayIfMidnight) {
      result = time === '00:00:00' ? result.add(1, 'day') : result;
    }

    return result;
  },
  useDateObject: (date: DateObject | string, timezone?: string) => React.useMemo(() => {
    const result = dayjs(date);

    if (timezone) {
      return result.tz(timezone);
    }

    return result;
  }, [ date, timezone ]),
  isDateObject: dayjs.isDayjs,
  compare: (a: DateObject | string, b: DateObject | string) => dayjs(a).isSameOrAfter(b) ? 1 : -1,
  inRange: (date: string | DateObject, range: TimeWindow) => {
    const _date = dayjs(date);

    return _date.isSameOrAfter(range.start, 'date') && _date.isBefore(range.end, 'date');
  },
  range,
  splitDateTime,
  timeRangeToTimeWindow,
  timeWindowToTimeRange,
  squashTimeRanges,
  now,
  getSecondsToNextMinute,
  timezoneAbbreviation,
  getMonthDatesRange,
  isTodayOrFutureDay,
  setHour,
  addHour,
  setMinutes,
  format,
  setTimeZone,
  getReschedulingTime,
  timezones: abbreviationsToTz,
};
